import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Autocomplete,
  TextField,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { withTranslation } from "react-i18next";

import InputFieldText from "Components/Common/InputFieldText";
import { applicationApprovalPendingStatus } from "common/enums";
import {
  CONSUMER,
  DCS_DEPARTMENT_HEAD,
  DCS_FINANCE,
  POWER_DEPARTMENT_HEAD,
  SEWAGE_DEPARTMENT_HEAD,
  SEWAGE_FINANCE,
  SOLID_WASTE_DEPARTMENT_HEAD,
  SOLID_WASTE_FINANCE,
  WATER_DEPARTMENT_HEAD,
  WATER_FINANCE,
  dcsAdmin3Roles,
  dcsAdmin4Roles,
  dcsApprovedByAccountsDeptRole,
  dcsApprovedByDirectorAndGroupCEORole,
  dcsApprovedByEngineeringDeptRole,
  dcsPreparedByEngineeringDeptRole,
  otherUtilitiesAdmin3Roles,
  otherUtilitiesAdmin4Roles,
  otherUtilitiesApprovedByAccountsDeptRole,
  otherUtilitiesApprovedByDirectorAndGroupCEORole,
  otherUtilitiesApprovedByEngineeringDeptRole,
  otherUtilitiesPreparedByEngineeringDeptRole,
  powerAdmin3Roles,
  powerAdmin4Roles,
  powerApprovedByAccountsDeptRole,
  powerApprovedByDirectorAndGroupCEORole,
  powerApprovedByEngineeringDeptRole,
  powerPreparedByEngineeringDeptRole,
} from "common/constant";
import TechnicalSanctionActionModal from "pages/Admin/PowerApplicationRequest/TechnicalSanctionActionModal";
import DemandEstimateActionModal from "pages/Admin/PowerApplicationRequest/DemandEstimateActionModal";

import { isAuthorizedUser } from "utils";

import { useApplicantStatusQuery } from "apis/ApplicationStatus";
import { useApplicationTypeQuery } from "apis/ApplicationType";
import TaxInvoiceActionModal from "pages/Admin/PowerApplicationRequest/TaxInvoiceActionModal";

const technicalSanctionOptions = [
  { label: "All", value: "" },
  { label: "Approval Application Pending", value: true },
];

const initialFilters = {
  selectedStatusId: "",
  selectedStartDate: null,
  selectedEndDate: null,
  selectedApplicationTypeId: "",
  applicationNumber: "",
  applicantName: "",
  consumerNumber: "",
  showPendingApprovalApplications: "",
  isPendingTechnicalSanctionApprovals: "",
};

const rolesToCheck = [
  ...powerAdmin3Roles,
  ...powerAdmin4Roles,
  ...dcsAdmin3Roles,
  ...dcsAdmin4Roles,
  ...otherUtilitiesAdmin3Roles,
  ...otherUtilitiesAdmin4Roles,
];

const rolesToCheckForTechnicalSanction = [
  ...powerPreparedByEngineeringDeptRole,
  ...powerApprovedByAccountsDeptRole,
  ...powerApprovedByEngineeringDeptRole,
  ...powerApprovedByDirectorAndGroupCEORole,
  ...dcsPreparedByEngineeringDeptRole,
  ...dcsApprovedByDirectorAndGroupCEORole,
  ...dcsApprovedByEngineeringDeptRole,
  ...dcsApprovedByAccountsDeptRole,
  ...otherUtilitiesApprovedByDirectorAndGroupCEORole,
  ...otherUtilitiesApprovedByEngineeringDeptRole,
  ...otherUtilitiesPreparedByEngineeringDeptRole,
  ...otherUtilitiesApprovedByAccountsDeptRole,
];

const allowRolesForBulkApplicationApproval = [
  POWER_DEPARTMENT_HEAD,
  DCS_DEPARTMENT_HEAD,
  WATER_DEPARTMENT_HEAD,
  SEWAGE_DEPARTMENT_HEAD,
  SOLID_WASTE_DEPARTMENT_HEAD,
];

const allowedRolesForBulkTaxInvoiceApproval = [
  DCS_FINANCE,
  WATER_FINANCE,
  SEWAGE_FINANCE,
  SOLID_WASTE_FINANCE,
];

const FilterApplicationRequests = ({
  setQuery,
  resetPage = () => {},
  t,
  isPowerUtility = false,
}) => {
  const { data: { data: status = [] } = {} } = useApplicantStatusQuery();
  const { data: { data: { items: applicationType = [] } = {} } = {} } =
    useApplicationTypeQuery();

  const isExtraLargeScreen = useMediaQuery("(max-width: 1620px)");

  const [
    {
      selectedStatusId,
      selectedApplicationTypeId,
      selectedStartDate,
      selectedEndDate,
      applicationNumber,
      consumerNumber,
      applicantName,
      showPendingApprovalApplications,
      isPendingTechnicalSanctionApprovals,
    },
    setSelectedFilteredOption,
  ] = useState(initialFilters);

  const [statusOptions, setStatusOptions] = useState([]);
  const [applicationTypeOptions, setApplicationTypeOptions] = useState([]);

  const [
    isTechnicalSanctionActionModalOpen,
    setIsTechnicalSanctionActionModalOpen,
  ] = useState(false);

  const [isTaxInvoiceModalOpen, setIsTaxInvoiceModalOpen] = useState(false);

  const [isDemandEstimateActionModalOpen, setIsDemandEstimateActionModalOpen] =
    useState(false);
  const [invalidDateMessage, setInvalidDateMessage] = useState({
    startDateErrorMessage: "",
    endDateErrorMessage: "",
  });

  const { roles } = useSelector((state) => state.Login);

  const changeSelectOptions = (option) => {
    return option.map((option) => ({
      label: option.name,
      value: option.id,
    }));
  };

  useEffect(() => {
    const options = changeSelectOptions(status);
    setStatusOptions([{ label: "All", value: "" }, ...options]);
  }, [status]);

  useEffect(() => {
    const options = changeSelectOptions(applicationType);
    setApplicationTypeOptions([{ label: "All", value: "" }, ...options]);
  }, [applicationType]);

  const clearBtnHandler = () => {
    resetPage();
    // setIsApplicationNumberValid(true);
    setSelectedFilteredOption(initialFilters);
    setQuery((query) => ({
      utilities: query.utilities,
    }));
  };

  const searchBtnHandler = (e) => {
    e.preventDefault();
    resetPage();
    setQuery((query) => {
      return {
        ...query,
        searchStatus: selectedStatusId,
        applicationTypeId: selectedApplicationTypeId,
        fromDate: selectedStartDate?.isValid()
          ? selectedStartDate.format("MM-DD-YYYY")
          : "",
        toDate: selectedEndDate?.isValid()
          ? selectedEndDate.format("MM-DD-YYYY")
          : "",
        applicationNumber: applicationNumber,
        consumerNumber,
        applicantName,
        isPendingApplicationApproval: showPendingApprovalApplications,
        isPendingTechnicalSanctionApprovals,
      };
    });
  };

  const errorMessage = (error) => {
    switch (error) {
      case "maxDate":
        return t("Please select date that is before end date");
      case "minDate":
        return t("Please select date that is after start date");
      case "disableFuture":
        return t("You can't select future date");
      case "invalidDate":
        return t("Please enter a valid date");
      case "min1900":
        return t("Select a date that is subsequent than 01/01/1900");
      default:
        return "";
    }
  };

  // useEffect(() => {
  //   if (applicationNumber) {
  //     const applicationNumberRegex = /^[A-Z]{3}\d{11}$/;
  //     setIsApplicationNumberValid(
  //       applicationNumberRegex.test(applicationNumber)
  //     );
  //   }
  // }, [applicationNumber]);

  return (
    <>
      {isTechnicalSanctionActionModalOpen && (
        <TechnicalSanctionActionModal
          isPowerUtility={isPowerUtility}
          handleClose={() => setIsTechnicalSanctionActionModalOpen(false)}
        />
      )}

      {isDemandEstimateActionModalOpen && (
        <DemandEstimateActionModal
          isPowerUtility={isPowerUtility}
          handleClose={() => setIsDemandEstimateActionModalOpen(false)}
        />
      )}

      {isTaxInvoiceModalOpen && (
        <TaxInvoiceActionModal
          isPowerUtility={isPowerUtility}
          handleClose={() => setIsTaxInvoiceModalOpen(false)}
        />
      )}

      <form onSubmit={searchBtnHandler}>
        <Grid container columnSpacing={2} className="mt-2">
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <Autocomplete
              className="w-100 mb-2"
              size="small"
              value={
                applicationTypeOptions?.find(
                  (application) =>
                    application.value === selectedApplicationTypeId
                ) || ""
              }
              disableClearable
              options={applicationTypeOptions}
              renderInput={(params) => (
                <TextField {...params} label={t("Application Type")} />
              )}
              onChange={(_, option) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedApplicationTypeId: option.value,
                }))
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <Autocomplete
              className="w-100 mb-2"
              size="small"
              options={statusOptions}
              value={
                statusOptions?.find(
                  (status) => status.value === selectedStatusId
                ) || ""
              }
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={t("Select Status")} />
              )}
              onChange={(_, option) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStatusId: option.value,
                }))
              }
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                );
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("Start Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={selectedStartDate ?? null}
              disableFuture
              maxDate={selectedEndDate}
              onError={(error) =>
                setInvalidDateMessage((prevState) => ({
                  ...prevState,
                  startDateErrorMessage:
                    error === "minDate" ? "min1900" : error,
                }))
              }
              slotProps={{
                textField: {
                  size: "small",
                  helperText: errorMessage(
                    invalidDateMessage.startDateErrorMessage
                  ),
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedStartDate: date,
                }))
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <DatePicker
              label={t("End Date")}
              className="w-100 mb-2"
              format="DD-MM-YYYY"
              value={selectedEndDate ?? null}
              disableFuture
              onError={(error) => {
                setInvalidDateMessage((prevState) => ({
                  ...prevState,
                  endDateErrorMessage: error,
                }));
              }}
              minDate={selectedStartDate}
              slotProps={{
                textField: {
                  size: "small",
                  helperText: errorMessage(
                    invalidDateMessage.endDateErrorMessage
                  ),
                },
              }}
              onChange={(date) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  selectedEndDate: date,
                }))
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={isExtraLargeScreen ? 3 : 2}
          >
            <InputFieldText
              label={t("Application Number")}
              className="w-100"
              value={applicationNumber ?? ""}
              placeholder={t("Enter Application Number")}
              // errorMessage={
              //   !isApplicationNumberValid && "Please enter valid application number"
              // }
              onChange={(e) =>
                setSelectedFilteredOption((prevState) => ({
                  ...prevState,
                  applicationNumber: e.target.value,
                }))
              }
            />
          </Grid>

          {!roles.includes(CONSUMER) && (
            <>
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={isExtraLargeScreen ? 3 : 2}
              >
                <InputFieldText
                  label={t("Consumer Number")}
                  className="w-100"
                  value={consumerNumber ?? null}
                  placeholder={t("Enter Consumer Number")}
                  onChange={(e) =>
                    setSelectedFilteredOption((prevState) => ({
                      ...prevState,
                      consumerNumber: e.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={isExtraLargeScreen ? 3 : 2}
              >
                <InputFieldText
                  label={t("Consumer Name")}
                  className="w-100"
                  value={applicantName ?? null}
                  placeholder={t("Enter Consumer Name")}
                  onChange={(e) =>
                    setSelectedFilteredOption((prevState) => ({
                      ...prevState,
                      applicantName: e.target.value,
                    }))
                  }
                />
              </Grid>
            </>
          )}

          {isAuthorizedUser(roles, rolesToCheck) && (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              xl={isExtraLargeScreen ? 3 : 2}
            >
              <Autocomplete
                className="w-100 mb-2"
                size="small"
                options={applicationApprovalPendingStatus}
                value={
                  applicationApprovalPendingStatus.find(
                    (status) => status.value === showPendingApprovalApplications
                  ) || ""
                }
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Application Approval Pending")}
                  />
                )}
                onChange={(_, { value }) => {
                  setSelectedFilteredOption((prevState) => ({
                    ...prevState,
                    showPendingApprovalApplications: value,
                  }));
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  );
                }}
              />
            </Grid>
          )}

          {isAuthorizedUser(roles, rolesToCheckForTechnicalSanction) && (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              xl={isExtraLargeScreen ? 3 : 2}
            >
              <Autocomplete
                className="w-100 mb-2"
                size="small"
                options={technicalSanctionOptions}
                value={technicalSanctionOptions.find(
                  (status) =>
                    status.value === isPendingTechnicalSanctionApprovals
                )}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Technical Sanction Approval Pending")}
                  />
                )}
                onChange={(_, { value }) => {
                  setSelectedFilteredOption((prevState) => ({
                    ...prevState,
                    isPendingTechnicalSanctionApprovals: value,
                  }));
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.value}>
                      {option.label}
                    </li>
                  );
                }}
              />
            </Grid>
          )}

          <Grid item>
            <Button
              variant="outlined"
              type="submit"
              className="me-3 mb-2"
              disabled={
                !!(
                  invalidDateMessage.startDateErrorMessage ||
                  invalidDateMessage.endDateErrorMessage
                )
              }
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="me-3 mb-2"
            >
              {t("Clear")}
            </Button>
          </Grid>

          {isAuthorizedUser(roles, allowRolesForBulkApplicationApproval) && (
            <>
              <Grid item>
                <Button
                  variant="contained"
                  className="me-3 mb-2"
                  onClick={() => setIsTechnicalSanctionActionModalOpen(true)}
                >
                  {t("Bulk Technical Sanctions Approval")}
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  className="me-3 mb-2"
                  onClick={() => setIsDemandEstimateActionModalOpen(true)}
                >
                  {t("Bulk Demand Estimates Approval")}
                </Button>
              </Grid>
            </>
          )}

          {!isPowerUtility &&
            isAuthorizedUser(roles, allowedRolesForBulkTaxInvoiceApproval) && (
              <Grid item>
                <Button
                  variant="contained"
                  className="me-3 mb-2"
                  onClick={() => setIsTaxInvoiceModalOpen(true)}
                >
                  {t("Bulk Tax Invoice Approval")}
                </Button>
              </Grid>
            )}
        </Grid>
      </form>
    </>
  );
};

FilterApplicationRequests.propTypes = {
  setQuery: PropTypes.func,
  resetPage: PropTypes.func,
  t: PropTypes.func,
  isPowerUtility: PropTypes.bool,
};

export default withTranslation()(FilterApplicationRequests);
